import { useMediaQuery, useTheme } from '@mui/material';
import { RefObject } from 'react';

import { ChatSideContentLinkInterceptor } from '@/modules/common/Chat/ChatSideContent/ChatSideContentLinkInterceptor';
import { ChatSideContentProvider } from '@/modules/common/Chat/ChatSideContent/context/ChatSideContent.provider';

import { useHouseholdChatContext } from '../contexts/householdChat.context';
import { HouseholdChatDocked } from './HouseholdChatDocked';
import { HouseholdChatOverlay } from './HouseholdChatOverlay';

interface HouseholdChatProps {
  /**
   * The content area below the app bar (page column + chat). The resize hooks
   * measure this container to translate drag positions into pane widths.
   *
   * Optional only to satisfy the AppBarLayout slot types (slot props are
   * Partial); it must always be provided for this component to render.
   */
  containerRef?: RefObject<HTMLDivElement | null>;
  host?: 'layout' | 'fullScreenModal';
  /** Let modal chat use the narrow-screen overlay. Layout chat always does. */
  responsiveInModal?: boolean;
  modalHeaderHeight?: number;
}

/**
 * The width (px) at or above which the chat renders in the docked host; below
 * it, the chat falls back to the overlay host's SideDrawer. Set explicitly
 * rather than via a named theme breakpoint so the boundary is exactly 1440px.
 */
const CHAT_OVERLAY_BREAKPOINT_PX = 1440;

/**
 * The household chat entry point. At 1440px and up it renders the chat shell in
 * the docked host — a resizable sidebar in the household layout's
 * right-sidebar slot; below 1440px it falls back to the overlay host's
 * SideDrawer so the page content isn't squeezed below its minimum usable
 * width. Both hosts open a side content panel (cited documents, document
 * summaries) beside the chat column — its own surface with its own header,
 * always overlaying the page content rather than pushing it.
 */
export function HouseholdChat({
  containerRef,
  host = 'layout',
  responsiveInModal = false,
  modalHeaderHeight,
}: HouseholdChatProps) {
  if (!containerRef) {
    throw new Error('containerRef is required');
  }

  return (
    <HouseholdChatInner
      containerRef={containerRef}
      host={host}
      responsiveInModal={responsiveInModal}
      modalHeaderHeight={modalHeaderHeight}
    />
  );
}

interface HouseholdChatInnerProps {
  containerRef: RefObject<HTMLDivElement | null>;
  host: NonNullable<HouseholdChatProps['host']>;
  responsiveInModal: boolean;
  modalHeaderHeight?: number;
}

function HouseholdChatInner({
  containerRef,
  host,
  responsiveInModal,
  modalHeaderHeight,
}: HouseholdChatInnerProps) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(
    theme.breakpoints.down(CHAT_OVERLAY_BREAKPOINT_PX)
  );
  const { conversationId, isChatOpen, isFullScreenModalHostMounted, view } =
    useHouseholdChatContext();

  const isActiveHost =
    host === 'fullScreenModal'
      ? isFullScreenModalHostMounted
      : !isFullScreenModalHostMounted;

  // Keep the active host mounted so its drawer or Collapse can finish closing.
  if (!isActiveHost) {
    return null;
  }

  const shouldRenderDocked =
    (host === 'fullScreenModal' && !responsiveInModal) || !isMediumScreen;

  const chat = shouldRenderDocked ? (
    <HouseholdChatDocked containerRef={containerRef} isOpen={isChatOpen} />
  ) : (
    <HouseholdChatOverlay
      containerRef={containerRef}
      isOpen={isChatOpen}
      fullScreenModal={host === 'fullScreenModal'}
      modalHeaderHeight={modalHeaderHeight}
    />
  );

  // Mount above the docked/overlay breakpoint switch so open content survives
  // the viewport crossing 1440px in either direction.
  //
  // The interceptor routes document-summary links into the panel; without it
  // those links degrade to plain text in the transcript.
  return (
    <ChatSideContentProvider
      // Side content is scoped to the conversation it was opened from:
      // leaving it (switching conversations, opening the history list,
      // starting a new chat) closes the pane.
      scopeKey={`${view}:${conversationId}`}
    >
      <ChatSideContentLinkInterceptor>{chat}</ChatSideContentLinkInterceptor>
    </ChatSideContentProvider>
  );
}
